import React, { createContext, useContext, useState, ReactNode } from "react";
import { useWindowDimensions } from "react-native";

type WorkspaceThreadsContextType = {
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (id: string | null) => void;
  openWorkspaceId: string | null;
  setOpenWorkspaceId: (id: string | null) => void;
  toggleWorkspaceDrawer: (workspaceId: string) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
};

const WorkspaceThreadsContext = createContext<WorkspaceThreadsContextType>({
  activeThreadId: null,
  setActiveThreadId: () => {},
  activeWorkspaceId: null,
  setActiveWorkspaceId: () => {},
  openWorkspaceId: null,
  setOpenWorkspaceId: () => {},
  toggleWorkspaceDrawer: () => {},
  isSidebarCollapsed: false,
  setIsSidebarCollapsed: () => {},
});

export const WorkspaceThreadsProvider = ({ children }: { children: ReactNode }) => {
  const { width } = useWindowDimensions();
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [openWorkspaceId, setOpenWorkspaceId] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return width < 768; // collapse when initial width < 768
  });

  const toggleWorkspaceDrawer = (workspaceId: string) => {
    if (openWorkspaceId === workspaceId) {
      setOpenWorkspaceId(null);
    } else {
      setOpenWorkspaceId(workspaceId);
    }
  };

  return (
    <WorkspaceThreadsContext.Provider
      value={{
        activeThreadId,
        setActiveThreadId,
        activeWorkspaceId,
        setActiveWorkspaceId,
        openWorkspaceId,
        setOpenWorkspaceId,
        toggleWorkspaceDrawer,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
      }}>
      {children}
    </WorkspaceThreadsContext.Provider>
  );
};

export const useWorkspaceThreads = () => {
  const context = useContext(WorkspaceThreadsContext);
  if (!context) {
    throw new Error("useWorkspaceThreads must be used within a WorkspaceThreadsProvider");
  }
  return context;
};
